import { API_BASE_URL } from './constants';
import { getVisitorId } from './visitor-id';

/**
 * First-party product-interaction capture (web).
 *
 * `trackProductEvent(productId, event)` records one interaction so the backend
 * can power "your latest views" and price-tracking interest. There are two
 * transports behind that one call, chosen by whether anyone is signed in.
 *
 * Signed in: one row per call to `POST ${API_BASE_URL}/api/account/me/product-events`,
 * exactly as before. Auth is the `cch_customer_session` cookie (same as every
 * other `/api/account/*` call on web, see `account-api.ts`) sent via
 * `credentials: 'include'`, gated on the module-level customer id that
 * AuthContext mirrors in (the cookie itself is HttpOnly, so this flag is how
 * client code knows a session exists).
 *
 * Signed out: batched to the public `POST ${API_BASE_URL}/api/visitors/events`
 * carrying the visitor id, which used to be a silent no-op. Batched at most
 * every 2 seconds, or immediately at 20 queued, which is the endpoint's cap.
 * The queue is flushed when the page is hidden, and again when the person
 * signs in, so nothing queued before a sign-in is sent after the Worker has
 * already claimed that visitor's rows.
 *
 * Transport for the signed-in path is `fetch` with `keepalive: true` so the
 * request survives the page navigating away to a merchant site without
 * delaying that navigation. `navigator.sendBeacon` is deliberately NOT used
 * there: a beacon carrying an `application/json` Blob to the cross-origin
 * Worker needs a CORS preflight, which beacons cannot perform, so the browser
 * would drop it. Keepalive fetch gives the same survive-navigation guarantee
 * with working CORS and cookies.
 *
 * The visitor flush on page-hide is the one case a beacon wins, because a
 * hidden page may be frozen before a fetch is scheduled. It is sent as a
 * `text/plain` Blob for the same preflight reason: `text/plain` is a
 * CORS-safelisted content type and goes out as a simple request, where
 * `application/json` would be preflighted and dropped. The body is still JSON,
 * so the Worker parses it the same way. Anything else falls back to
 * `keepalive` fetch with a normal JSON content type.
 *
 * Strictly fire-and-forget throughout: returns synchronously, never throws,
 * swallows every failure. Interaction capture must never affect the UX, and
 * above all must never touch, delay, or gate the affiliate click-through.
 */

export type ProductEventType = 'view' | 'shop_click';

type VisitorEventName = 'product_view' | 'shop_click';

interface VisitorEvent {
  event: VisitorEventName;
  productId: string;
}

/** The endpoint accepts 1 to 20 events per call. */
const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 2000;

// Mirrored in by AuthContext (same pattern as setAffiliateCustomerId).
let signedInCustomerId: string | null = null;

let queue: VisitorEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let hideListenerAttached = false;

export function setProductEventsCustomerId(id: string | null): void {
  const wasSignedOut = signedInCustomerId === null;
  // Sign-in flushes first, so queued visitor activity reaches the Worker
  // under the visitor id rather than trailing in after the claim.
  if (id && wasSignedOut) flushVisitorProductEvents();
  signedInCustomerId = id;
}

function cancelTimer(): void {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
}

function attachHideListener(): void {
  if (hideListenerAttached) return;
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushVisitorProductEvents({ beacon: true });
      }
    });
    hideListenerAttached = true;
  } catch {
    // No document (SSR or prerender). Nothing queues there either.
  }
}

function post(visitorId: string, events: VisitorEvent[], beacon: boolean): void {
  const url = `${API_BASE_URL}/api/visitors/events`;
  const body = JSON.stringify({ visitorId, surface: 'web', events });

  if (beacon) {
    try {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(url, blob)) return;
    } catch {
      // Fall through to fetch.
    }
  }

  try {
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {
      // Best-effort capture: never let a failed send affect the page.
    });
  } catch {
    // e.g. fetch unavailable in an exotic environment, ignore.
  }
}

/**
 * Send everything queued now. Called on the 2 second timer, at the batch cap,
 * when the page is hidden, and on sign-in.
 */
export function flushVisitorProductEvents(
  options: { beacon?: boolean } = {},
): void {
  cancelTimer();
  if (queue.length === 0) return;

  const visitorId = getVisitorId();
  if (!visitorId) {
    // Consent was withdrawn, or no store will hold an id. Drop rather than
    // send something the Worker could never attach to anyone.
    queue = [];
    return;
  }

  const pending = queue;
  queue = [];
  for (let i = 0; i < pending.length; i += MAX_BATCH) {
    post(visitorId, pending.slice(i, i + MAX_BATCH), options.beacon === true);
  }
}

function queueVisitorEvent(productId: string, event: ProductEventType): void {
  // Minting the id is itself consent-gated, so this is where a visitor with
  // analytics consent declined stops: nothing is queued and nothing is sent.
  if (!getVisitorId()) return;

  queue.push({
    event: event === 'view' ? 'product_view' : 'shop_click',
    productId,
  });
  attachHideListener();

  if (queue.length >= MAX_BATCH) {
    flushVisitorProductEvents();
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushVisitorProductEvents();
    }, FLUSH_INTERVAL_MS);
  }
}

export function trackProductEvent(
  productId: string,
  event: ProductEventType,
): void {
  if (!productId) return;

  if (!signedInCustomerId) {
    queueVisitorEvent(productId, event);
    return;
  }

  try {
    void fetch(`${API_BASE_URL}/api/account/me/product-events`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: productId, event }),
    }).catch(() => {
      // Best-effort capture: never let a failed send affect the page.
    });
  } catch {
    // e.g. fetch unavailable in an exotic environment, ignore.
  }
}
